import {IInputEditProps, InputEdit} from '@mintdata/mint-kit/impl/properties/components/inputEdit/InputEdit';
import {IPillProps, Pill} from '@mintdata/mint-kit/impl/properties/components/pill/Pill';
import {CommonUtils} from '@mintdata/mint-kit/impl/util/CommonUtils';

import * as Keys from '@mintdata/mint-kit/impl/util/Keys';
import * as classnames from 'classnames/bind';
import _isFunction from 'lodash/isFunction';
import * as React from 'react';

const styles = require('@mintdata/mint-kit/impl/properties/components/pillInput/PillInput.less');
const cx = classnames.bind(styles);

export interface IPillInputProps
{
    className?:string;
    inputProps?:Partial<IInputEditProps>;
    pillProps?:IPillProps;
    values:string[];
    pillRenderer?:(value:string) => JSX.Element;
    pillClassNameGetter?:(value:string) => string;

    onAdd?:(value:string) => boolean | void;
    onRemove?:(value:string, index:number) => void;
}

export interface IPillInputState
{
    activeIndex?:number;
    inputValue?:string;
}

const INDEX_NONE = -1;
const INDEX_OUTSIDE = 999999;

export class PillInput extends React.Component<IPillInputProps, IPillInputState>
{
    public static defaultProps:Partial<IPillInputProps> = {
        inputProps: {},
        pillProps: {},
    };

    public state:IPillInputState = {
        activeIndex: INDEX_NONE,
        inputValue: this._getInitialInputValue(),
    };

    private _input:HTMLInputElement | null;
    private _refHandlers = {
        input: (ref:HTMLInputElement | null) =>
        {
            const {inputProps} = this.props;
            this._input = ref;

            if( _isFunction(inputProps.onRef) )
            {
                inputProps.onRef(ref);
            }
        },
    };

    public componentWillReceiveProps(nextProps:IPillInputProps)
    {
        const {inputProps} = nextProps;

        if( inputProps.value !== undefined )
        {
            this.setState({inputValue: inputProps.value});
        }
    }

    public render()
    {
        const {className, inputProps, values, children} = this.props;
        const {inputValue} = this.state;

        const {
            className: inputClassName,
            ...restInputProps
        } = inputProps;

        return (
            <div className={cx('base', className)} onClick={this._handleContainerClick}>
                {values.map(this._renderPill)}
                {children}
                <div className={styles.input}>
                    <InputEdit
                        className={cx('inputEdit', inputClassName)}
                        value={inputValue}
                        {...restInputProps}
                        onChange={this._handleInputChange}
                        onKeyDown={this._handleInputKeyDown}
                        onRef={this._refHandlers.input}
                    />
                </div>
            </div>
        );
    }

    private _getInitialInputValue()
    {
        const {inputProps} = this.props;

        return inputProps.value !== undefined ? inputProps.value : '';
    }

    private _renderPill = (pill:string, index:number) =>
    {
        if( !pill )
        {
            return null;
        }

        const {pillProps, pillRenderer, pillClassNameGetter} = this.props;
        const {activeIndex} = this.state;

        const {
            className: pillClassName,
            ...restPillProps
        } = pillProps;

        return (
            <Pill
                key={index}
                index={index}
                activeIndex={activeIndex}
                active={index === activeIndex}
                onFocus={(index) => this.setState({activeIndex: index})}
                onBlur={() => this.setState({activeIndex: INDEX_NONE})}
                className={cx(pillClassNameGetter ? pillClassNameGetter(pill) : null, pillClassName)}
                onRemove={this._getRemovePillHandler(index)}
                {...restPillProps}
            >
                {_isFunction(pillRenderer) ? pillRenderer(pill) : pill}
            </Pill>
        );
    };

    private _addPill = (value:string) =>
    {
        const {onAdd} = this.props;
        let shouldClearInput:boolean | void = true;

        if( _isFunction(onAdd) )
        {
            shouldClearInput = onAdd(value);
        }

        if( shouldClearInput !== false )
        {
            this.setState({inputValue: ''});
        }
    };

    private _handleContainerClick = () =>
    {
        if( this._input != null )
        {
            this._input.focus();
        }
    };

    private _handleInputChange = (event:React.FormEvent<HTMLInputElement>) =>
    {
        const {inputProps} = this.props;

        this.setState({activeIndex: INDEX_NONE, inputValue: event.currentTarget.value});

        if( _isFunction(inputProps.onChange) )
        {
            inputProps.onChange(event);
        }
    };

    private _handleInputKeyDown = (event:React.KeyboardEvent<HTMLInputElement>) =>
    {
        const {values, inputProps} = this.props;
        const {activeIndex} = this.state;
        const {selectionEnd, value} = event.currentTarget;

        if( event.which === Keys.ENTER && value.length > 0 )
        {
            this._addPill(value);
        }
        else if( selectionEnd === 0 && values.length > 0 )
        {
            if( event.which === Keys.ARROW_LEFT || event.which === Keys.ARROW_RIGHT )
            {
                const nextActiveIndex = this._getNextActiveIndex(event.which === Keys.ARROW_RIGHT ? 1 : -1);

                if( nextActiveIndex !== activeIndex )
                {
                    event.stopPropagation();
                    this.setState({activeIndex: nextActiveIndex});
                }
            }
            else if( event.which === Keys.BACKSPACE )
            {
                this._handleBackspaceToRemove(event);
            }
        }

        if( _isFunction(inputProps.onKeyDown) )
        {
            inputProps.onKeyDown(event);
        }
    };

    private _handleBackspaceToRemove(event:React.KeyboardEvent<HTMLInputElement>)
    {
        const previousActiveIndex = this.state.activeIndex;
        const nextActiveIndex = this._getNextActiveIndex(-1);

        if( this._isValidIndex(previousActiveIndex) )
        {
            event.stopPropagation();
            this._removeIndexFromValues(previousActiveIndex);
        }

        this.setState({activeIndex: nextActiveIndex});
    }

    private _getRemovePillHandler = (index:number) =>
    {
        return (event:React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLButtonElement>) =>
        {
            this._removeIndexFromValues(index);
        };
    };

    private _getNextActiveIndex(direction:number)
    {
        const {values} = this.props;
        const {activeIndex} = this.state;

        if( direction > 0 && activeIndex >= values.length - 1)
        {
            return INDEX_OUTSIDE;
        }

        if( activeIndex === INDEX_NONE )
        {
            return direction < 0 ? values.length - 1 : INDEX_NONE;
        }
        else
        {
            return CommonUtils.clamp(activeIndex + direction, 0, values.length);
        }
    }

    private _isValidIndex(index:number)
    {
        return index !== INDEX_NONE && index < this.props.values.length;
    }

    private _removeIndexFromValues(index:number)
    {
        const {onRemove, values} = this.props;

        onRemove(values[index], index);
    }
}

